import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Gauge, Target, SlidersHorizontal, ShieldAlert } from 'lucide-react';
import { Card, Button, Pill, PhaseGate, Input, Textarea, Select, Label, FieldGroup } from '../';
import { useToast } from '../../../contexts/ToastContext';
import * as api from '../../../services/api';
import SkillPromotionCard from '../skill/SkillPromotionCard';
import SkillEvalPanel from '../skill/SkillEvalPanel';

/**
 * ExteletDecideTab — Prove 5 (Decide) for external agents.
 *
 * Replaces the native skill-review DecideTab for ExteLets, which has no
 * native Skill / SkillReview rows and therefore renders a permanently-red
 * decision matrix (the X1 defect). Per EXTELET_7PROVE_JOURNEY §6, Decide for
 * an ExteLet is three things plus a gate:
 *
 *   1. Autonomy ceiling — how much autonomy the agent gets
 *      (Autonomous / Supervised / Restricted / Blocked), set against risk.
 *   2. Trust baseline — register the conformance baseline (The Crossing): the
 *      reference the agent's live behavior is scored against.
 *   3. Scoring config — the Org→Tenant→Agent inheritance (window, thresholds,
 *      retention), shown read-only with a link to the full scoring surface.
 *
 * The Decide → Act gate shown here is the SAME gate
 * PhaseGateService.evaluateDecideGateExternal previews and
 * ExternalAgentLifecycleService.advanceToAct enforces — displayed == enforced.
 *
 * @param {Object} props
 * @param {Object} props.agent          the agent (needs id/tenantId/orgId)
 * @param {Object} props.registration   the external_agent_registration row
 * @param {Function} props.reloadRegistration  refetch the registration
 * @param {Function} [props.reloadAgentFromParent]  refetch the agent
 */
export default function ExteletDecideTab({ agent, registration, reloadRegistration, reloadAgentFromParent }) {
  const { id } = useParams();
  const agentId = id || agent?.id;
  const tenantId = agent?.tenantId;
  const orgId = agent?.orgId;
  const registrationId = registration?.id;

  const toastCtx = useToast();
  const addToast = useCallback((arg) => {
    if (toastCtx && typeof toastCtx.addToast === 'function') return toastCtx.addToast(arg);
    // eslint-disable-next-line no-console
    console.warn('[ExteletDecideTab] toast unavailable, dropped:', arg);
    return null;
  }, [toastCtx]);

  // ── Autonomy ceiling ──────────────────────────────────────────────
  const AUTONOMY_OPTIONS = [
    { value: 'FULL',       label: 'Autonomous', desc: 'Acts without per-call approval; every call is still governed at the gateway.' },
    { value: 'SUPERVISED', label: 'Supervised', desc: 'Acts live; a human is notified and can intervene or roll back.' },
    { value: 'RESTRICTED', label: 'Restricted', desc: 'Each action needs human approval before it runs.' },
    { value: 'PROBATION',  label: 'Blocked',    desc: 'No live actions — observation only.' },
  ];

  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [pendingLevel, setPendingLevel] = useState(null);
  const [autonomyReason, setAutonomyReason] = useState('');
  const [savingAutonomy, setSavingAutonomy] = useState(false);

  const loadProfile = useCallback(async () => {
    if (!agentId) return;
    setProfileLoading(true);
    try {
      const res = await api.getTrustProfile(agentId);
      setProfile(res?.data ?? null);
    } catch (e) {
      setProfile(null);
    } finally {
      setProfileLoading(false);
    }
  }, [agentId]);

  // ── Conformance baseline + scoring config ─────────────────────────
  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [baselineForm, setBaselineForm] = useState({
    conformanceComparatorMode: 'exact_match',
    requiredConformance: '90',
    conformanceSampleSize: '50',
    conformanceWeight: '0.25',
    shadowConformanceEnabled: 'true',
  });
  const [savingBaseline, setSavingBaseline] = useState(false);

  const loadConfig = useCallback(async () => {
    if (!tenantId || !agentId) { setConfigLoading(false); return; }
    setConfigLoading(true);
    try {
      const res = await api.getTrustScoringConfig(tenantId, 'AGENT', agentId);
      const cfg = res?.data ?? null;
      setConfig(cfg);
      if (cfg) {
        setBaselineForm((prev) => ({
          conformanceComparatorMode: cfg.conformanceComparatorMode ?? prev.conformanceComparatorMode,
          requiredConformance: String(cfg.requiredConformance ?? prev.requiredConformance),
          conformanceSampleSize: String(cfg.conformanceSampleSize ?? prev.conformanceSampleSize),
          conformanceWeight: String(cfg.conformanceWeight ?? prev.conformanceWeight),
          shadowConformanceEnabled: String(cfg.shadowConformanceEnabled ?? prev.shadowConformanceEnabled),
        }));
      }
    } catch (e) {
      setConfig(null);
    } finally {
      setConfigLoading(false);
    }
  }, [tenantId, agentId]);

  // ── Golden baseline (reference oracle) ────────────────────────────
  // The real conformance oracle for an ExteLet: representative input → expected
  // output. On a matching governed call the agent's output is scored against this.
  const [goldenForm, setGoldenForm] = useState({ input: '', goldenOutput: '', comparatorMode: '', description: '' });
  const [goldenList, setGoldenList] = useState([]);
  const [goldenLoading, setGoldenLoading] = useState(true);
  const [savingGolden, setSavingGolden] = useState(false);

  const loadGolden = useCallback(async () => {
    if (!agentId) { setGoldenLoading(false); return; }
    setGoldenLoading(true);
    try {
      const res = await api.listConformanceBaselines(agentId);
      setGoldenList(Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []));
    } catch (e) {
      setGoldenList([]);
    } finally {
      setGoldenLoading(false);
    }
  }, [agentId]);

  const handleRegisterGolden = useCallback(async () => {
    let input; let goldenOutput;
    try {
      input = JSON.parse(goldenForm.input);
    } catch (e) {
      addToast({ tone: 'danger', title: 'Invalid input JSON', description: 'The representative input must be valid JSON.' });
      return;
    }
    try {
      goldenOutput = JSON.parse(goldenForm.goldenOutput);
    } catch (e) {
      addToast({ tone: 'danger', title: 'Invalid expected-output JSON', description: 'The expected output must be valid JSON.' });
      return;
    }
    setSavingGolden(true);
    try {
      const body = {
        input,
        goldenOutput,
        comparatorMode: goldenForm.comparatorMode || null,
        description: goldenForm.description || null,
      };
      const res = await api.registerConformanceBaseline(agentId, body);
      if (res && res.ok === false) throw new Error(res.error || 'Failed to register golden baseline');
      addToast({ tone: 'success', title: 'Golden baseline registered',
        description: 'Matching protected calls will now be conformance-scored against this output.' });
      setGoldenForm((p) => ({ ...p, input: '', goldenOutput: '', description: '' }));
      await Promise.all([loadGolden(), loadGate()]);
    } catch (e) {
      addToast({ tone: 'danger', title: 'Could not register golden baseline', description: e?.message || String(e) });
    } finally {
      setSavingGolden(false);
    }
    // Note: loadGate (defined later) is intentionally NOT in deps — referencing it
    // here would TDZ at render; it's a stable useCallback and only refetches gate state.
  }, [agentId, goldenForm, addToast, loadGolden]);

  const handleDeleteGolden = useCallback(async (baselineId) => {
    try {
      const res = await api.deleteConformanceBaseline(agentId, baselineId);
      if (res && res.ok === false) throw new Error(res.error || 'Delete failed');
      await loadGolden();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Could not delete baseline', description: e?.message || String(e) });
    }
  }, [agentId, addToast, loadGolden]);

  // ── Decide → Act gate (authoritative, from backend) ───────────────
  const [gateChecks, setGateChecks] = useState([]);
  const [gateLoading, setGateLoading] = useState(true);

  const loadGate = useCallback(async () => {
    if (!agentId) return;
    setGateLoading(true);
    try {
      const res = await api.previewPhaseGate(agentId);
      const checks = Array.isArray(res?.data?.checks) ? res.data.checks : [];
      setGateChecks(checks);
    } catch (e) {
      setGateChecks([{ check: 'preview_error', passed: false,
        message: e?.message || 'Failed to load the Decide gate preview' }]);
    } finally {
      setGateLoading(false);
    }
  }, [agentId]);

  useEffect(() => { loadProfile(); loadConfig(); loadGate(); loadGolden(); }, [loadProfile, loadConfig, loadGate, loadGolden]);

  // ── Handlers ──────────────────────────────────────────────────────
  const handleSetAutonomy = useCallback(async () => {
    if (!pendingLevel || !agentId) return;
    if (!autonomyReason.trim()) {
      addToast({ tone: 'danger', title: 'Reason required', description: 'Give a short justification for this autonomy ceiling.' });
      return;
    }
    setSavingAutonomy(true);
    try {
      const res = await api.manualAutonomyOverride(agentId, pendingLevel, autonomyReason.trim());
      if (res && res.ok === false) throw new Error(res.error || 'Failed to set autonomy');
      addToast({ tone: 'success', title: 'Autonomy ceiling set',
        description: `Set to ${AUTONOMY_OPTIONS.find((o) => o.value === pendingLevel)?.label || pendingLevel}.` });
      setPendingLevel(null);
      setAutonomyReason('');
      await Promise.all([loadProfile(), loadGate()]);
    } catch (e) {
      addToast({ tone: 'danger', title: 'Could not set autonomy', description: e?.message || String(e) });
    } finally {
      setSavingAutonomy(false);
    }
  }, [pendingLevel, agentId, autonomyReason, addToast, loadProfile, loadGate]);

  const num = (v, d) => (v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? d : Number(v));

  const handleRegisterBaseline = useCallback(async () => {
    if (!tenantId || !orgId || !agentId) {
      addToast({ tone: 'danger', title: 'Missing scope', description: 'Agent tenant/org not resolved yet.' });
      return;
    }
    const base = config || {};
    // An AGENT-scope override is a COMPLETE row (per TrustScoringConfig: each
    // saved config overrides all fields; inheritance is at resolution time).
    // Preserve the inherited scoring knobs and set only the baseline fields.
    const payload = {
      configScope: 'AGENT',
      tenantId,
      orgId,
      agentId,
      evaluationMultiplier: num(base.evaluationMultiplier, 1),
      blockWeight: num(base.blockWeight, 3),
      warnWeight: num(base.warnWeight, 1),
      auditWeight: num(base.auditWeight, 0.25),
      selfAutonomousThreshold: num(base.selfAutonomousThreshold, 98),
      selfAutonomousMinEvals: num(base.selfAutonomousMinEvals, 500),
      supervisedThreshold: num(base.supervisedThreshold, 90),
      restrictedThreshold: num(base.restrictedThreshold, 75),
      windowDays: num(base.windowDays, 90),
      probationDays: num(base.probationDays, 7),
      trustHistoryRetentionDays: num(base.trustHistoryRetentionDays, 1095),
      minEvalsForScoring: num(base.minEvalsForScoring, 50),
      scoreFloor: num(base.scoreFloor, 5),
      anomalyCooldownHours: num(base.anomalyCooldownHours, 6),
      autoRegressionOnBlock: base.autoRegressionOnBlock ?? true,
      targetTrustScore: num(base.targetTrustScore, 95),
      targetAutonomyLevel: base.targetAutonomyLevel || 'SUPERVISED',
      orgRiskTolerance: base.orgRiskTolerance || 'MODERATE',
      shadowSamplingRate: num(base.shadowSamplingRate, 1),
      // operator-set baseline knobs
      conformanceComparatorMode: baselineForm.conformanceComparatorMode,
      requiredConformance: num(baselineForm.requiredConformance, 90),
      conformanceSampleSize: parseInt(baselineForm.conformanceSampleSize, 10) || 50,
      conformanceWeight: num(baselineForm.conformanceWeight, 0.25),
      shadowConformanceEnabled: baselineForm.shadowConformanceEnabled === 'true'
        || baselineForm.shadowConformanceEnabled === true,
    };
    setSavingBaseline(true);
    try {
      const res = await api.updateTrustScoringConfig(payload);
      if (res && res.ok === false) throw new Error(res.error || 'Failed to register baseline');
      addToast({ tone: 'success', title: 'Conformance baseline registered',
        description: 'The agent will be scored against this baseline once live.' });
      await Promise.all([loadConfig(), loadGate()]);
    } catch (e) {
      addToast({ tone: 'danger', title: 'Could not register baseline', description: e?.message || String(e) });
    } finally {
      setSavingBaseline(false);
    }
  }, [tenantId, orgId, agentId, config, baselineForm, addToast, loadConfig, loadGate]);

  const [advancing, setAdvancing] = useState(false);
  const handleAdvanceToAct = useCallback(async () => {
    if (!registrationId) {
      addToast({ tone: 'danger', title: 'No registration', description: 'Registration not loaded yet.' });
      return;
    }
    setAdvancing(true);
    try {
      const res = await api.gelAdvanceToAct(registrationId);
      if (res && res.ok === false) {
        throw new Error(res.error || 'Advance to Act failed');
      }
      addToast({ tone: 'success', title: 'Advanced to Act', description: 'The agent is ready for its execution probe.' });
      if (typeof reloadRegistration === 'function') await reloadRegistration();
      if (typeof reloadAgentFromParent === 'function') await reloadAgentFromParent();
      await loadGate();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Could not advance to Act', description: e?.message || String(e) });
    } finally {
      setAdvancing(false);
    }
  }, [registrationId, addToast, reloadRegistration, reloadAgentFromParent, loadGate]);

  // ── Derived ───────────────────────────────────────────────────────
  const GATE_LABELS = {
    registration_exists: 'Registration on file',
    governance_attached: 'Governance attached',
    policy_enforceable: 'Governance is enforceable',
    autonomy_ceiling_set: 'Autonomy ceiling set',
    conformance_baseline_registered: 'Conformance baseline registered',
    at_least_one_reviewed_skill: 'At least one policy skill reviewed',
    autonomous_score_threshold: 'Autonomous skills meet score threshold',
    preview_error: 'Gate preview',
  };
  const criteria = gateChecks.map((c) => ({
    id: c.check,
    label: GATE_LABELS[c.check] || c.check,
    passed: !!c.passed,
    detail: c.message,
  }));
  // ── Skill review (Decision matrix) — DECIDE-SCOPED, rendered with the SHARED
  //    <SkillPromotionCard> (identical card across all agentlet types). The card
  //    owns review (slideout) / advance / regress via the shared backend; this tab
  //    just loads the policy skills and reloads on change. It shows only skills
  //    already promoted to Decide/Act — Build-phase skills stay on the Build tab.
  const [policySkills, setPolicySkills] = useState([]);
  const [skillsLoading, setSkillsLoading] = useState(true);
  const loadPolicySkills = useCallback(async () => {
    if (!agentId) return;
    setSkillsLoading(true);
    try {
      const res = await api.getAgentSkills?.(agentId);
      const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      setPolicySkills(list.filter((s) => s.skillType === 'policy_skill'));
    } finally { setSkillsLoading(false); }
  }, [agentId]);
  useEffect(() => { loadPolicySkills(); }, [loadPolicySkills]);
  const onSkillsChanged = useCallback(async () => {
    await Promise.all([loadPolicySkills(), loadGate()]);
    if (typeof reloadAgentFromParent === 'function') reloadAgentFromParent();
  }, [loadPolicySkills, loadGate, reloadAgentFromParent]);

  const advanceReady = !gateLoading && criteria.length > 0 && criteria.every((c) => c.passed);
  const passedCount = criteria.filter((c) => c.passed).length;

  // Phase-aware advance: the Decide → Act advance only makes sense while the
  // agent is actually in DECIDE. If it has already advanced (now in ACT), the
  // backend rejects the call ("Cannot advance to ACT from status: ACT") — so we
  // must NOT present a live Advance button. If it's still in BUILD, Decide → Act
  // isn't reachable yet either. This mirrors the native model, where advancing
  // switches tabs and the Decide gate's button keys off the real phase.
  // Source-of-truth note: the GEL gate keys off registration.registrationStatus,
  // while the header/Promote UI keys off agent.phase. These are kept in lockstep
  // by the lifecycle service — but to be defensive against any drift, treat the
  // agent as "already in Act" if EITHER says so, so we never present a live
  // Advance button the backend would reject with a phase mismatch.
  const agentPhaseLc = (agent?.phase || 'build').toLowerCase();
  const regStatusUc = (registration?.registrationStatus || '').toUpperCase();
  const alreadyInAct = agentPhaseLc === 'act' || regStatusUc === 'ACT';
  const notYetInDecide = agentPhaseLc === 'build' && regStatusUc !== 'DECIDE' && regStatusUc !== 'ACT';

  const currentLevel = profile?.currentAutonomyLevel || null;
  const currentLevelLabel = AUTONOMY_OPTIONS.find((o) => o.value === currentLevel)?.label || currentLevel || 'Not set';
  const autonomySet = criteria.find((c) => c.id === 'autonomy_ceiling_set')?.passed;
  const baselineSet = criteria.find((c) => c.id === 'conformance_baseline_registered')?.passed;

  return (
    <div className="space-y-5">
      {/* Decide → Act gate (displayed == enforced) */}
      <PhaseGate
        phase="DECIDE"
        criteria={criteria}
        advanceAction={
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-v2-ink-muted tabular-nums">
              Gate: {passedCount} of {criteria.length}
            </span>
            {alreadyInAct ? (
              <Pill tone="success" size="sm">Advanced to Act ✓</Pill>
            ) : (
              <Button
                variant="primary"
                size="sm"
                loading={advancing}
                disabled={!advanceReady || !registrationId || advancing || notYetInDecide}
                onClick={handleAdvanceToAct}
                title={notYetInDecide
                  ? 'Advance Build → Decide first'
                  : advanceReady
                    ? 'Advance the external agent from Decide to Act'
                    : 'Gate not passing — set the autonomy ceiling and register the conformance baseline below'}>
                Advance to Act
              </Button>
            )}
          </div>
        }
      />

      {/* 0 · Decision matrix · skill review — DECIDE-scoped. Reviewing a policy skill
          sets its autonomy, exactly like the native Let Decide tab. Build-phase
          tooling (Configure / Generate eval / → Decide) is NOT shown here — it lives
          on the Build tab. */}
      <Card padding="none">
        <div className="flex items-center gap-3 px-4 pt-4 pb-3 border-b border-v2-border-subtle">
          <SlidersHorizontal size={16} className="text-v2-cyan" />
          <h3 className="text-[14px] font-bold tracking-tight m-0">Decision matrix · skill review</h3>
          <span className="text-[11px] text-v2-ink-muted">
            {policySkills.length} policy skill{policySkills.length === 1 ? '' : 's'}
          </span>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-[12.5px] text-v2-ink-muted m-0">
            Reviewing a policy skill sets its autonomy: <strong>Autonomous</strong> (no-touch, needs eval ≥ 90%),
            <strong> Human approval</strong> (each call surfaces for approval), or <strong>Blocked</strong>. After a
            non-blocked review, promote it <strong>→ Act</strong>. At least one non-blocked review is required to
            advance the agent to Act.
          </p>

          {/* Bug C2 — when the agent is already in Act, new unreviewed policy
              skills must be reviewed + activated individually (parity with native). */}
          {alreadyInAct && (() => {
            const unrev = policySkills.filter((s) => !(s.reviewStatus && s.reviewStatus !== 'pending_review'));
            if (unrev.length === 0) return null;
            return (
              <div className="rounded-v2-md px-3 py-2 text-[12px]"
                style={{ background: 'var(--v2-warning-soft)', color: 'var(--v2-warning)' }}>
                <strong>{unrev.length} skill{unrev.length > 1 ? 's need' : ' needs'} review.</strong> This agent is
                already in the Act phase — review and approve each new skill individually to activate it alongside
                your existing skills.
              </div>
            );
          })()}
          {(() => {
            const decideAndAct = policySkills.filter((s) => (s.skillPhase || 'build').toLowerCase() !== 'build');
            const buildCount = policySkills.length - decideAndAct.length;
            /* PROVE7-020: spinner on INITIAL load only — a background refetch after an
               eval run must not blank the grid, else the card (and its open Eval slideout)
               unmounts. Cards reconcile by key=s.id; keeping them mounted matches native LET. */
            if (skillsLoading && policySkills.length === 0) return <p className="text-[12px] text-v2-ink-muted m-0">Loading skills…</p>;
            if (policySkills.length === 0) {
              return (
                <p className="text-[12px] text-v2-ink-muted m-0">
                  No policy skill yet. Add one on the Build tab, eval it, and promote it to Decide — then review it here.
                </p>
              );
            }
            return (
              <>
                {decideAndAct.length === 0 ? (
                  <p className="text-[12px] text-v2-ink-muted m-0">
                    No skills in Decide yet. Promote a skill to Decide on the <strong>Build</strong> tab, then review it here.
                  </p>
                ) : (
                  <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))' }}>
                    {decideAndAct.map((s) => (
                      <SkillPromotionCard
                        key={s.id}
                        agentId={agentId}
                        skill={s}
                        surface="decide"
                        onChanged={onSkillsChanged}
                        renderEval={(sk) => <SkillEvalPanel agentId={agentId} skill={sk} onChanged={onSkillsChanged} />}
                      />
                    ))}
                  </div>
                )}
                {buildCount > 0 && (
                  <div className="rounded-v2-md px-3 py-2 text-[11.5px] text-v2-ink-muted bg-v2-muted">
                    {buildCount} policy skill{buildCount > 1 ? 's are' : ' is'} still in <strong>Build</strong> — promote
                    {buildCount > 1 ? ' them' : ' it'} to Decide on the Build tab to review here.
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </Card>

      {/* 1 · Autonomy ceiling */}
      <Card padding="none">
        <div className="flex items-center gap-3 px-4 pt-4 pb-3 border-b border-v2-border-subtle">
          <Gauge size={16} className="text-v2-cyan" />
          <h3 className="text-[14px] font-bold tracking-tight m-0">Autonomy ceiling</h3>
          <Pill tone={autonomySet ? 'success' : 'muted'} size="sm">
            {autonomySet ? `Set · ${currentLevelLabel}` : 'Not set'}
          </Pill>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-[12.5px] text-v2-ink-muted m-0">
            How much autonomy this agent gets, decided against the risk of its capabilities. Every
            call remains protected at the gateway regardless of level.
          </p>
          <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
            {AUTONOMY_OPTIONS.map((opt) => {
              const isCurrent = currentLevel === opt.value;
              const isPending = pendingLevel === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => { setPendingLevel(opt.value); setAutonomyReason(''); }}
                  className={
                    'text-left rounded-v2-md border px-3 py-2.5 v2-focus-ring transition-colors ' +
                    (isPending
                      ? 'border-v2-cyan bg-v2-cyan-soft'
                      : isCurrent
                        ? 'border-v2-success bg-v2-success-soft'
                        : 'border-v2-border-subtle bg-v2-surface hover:border-v2-cyan')
                  }>
                  <span className="flex items-center gap-2">
                    <span className="text-[13px] font-semibold text-v2-ink">{opt.label}</span>
                    {isCurrent && <Pill tone="success" size="sm">Current</Pill>}
                  </span>
                  <span className="block text-[11.5px] text-v2-ink-muted mt-1 leading-relaxed">{opt.desc}</span>
                </button>
              );
            })}
          </div>
          {pendingLevel && (
            <div className="rounded-v2-md border border-v2-border-subtle bg-v2-muted p-3 space-y-2">
              <FieldGroup>
                <Label htmlFor="autonomy-reason">
                  Justification for setting autonomy to{' '}
                  <strong>{AUTONOMY_OPTIONS.find((o) => o.value === pendingLevel)?.label}</strong>
                </Label>
                <Input
                  id="autonomy-reason"
                  value={autonomyReason}
                  onChange={(e) => setAutonomyReason(e.target.value)}
                  placeholder="e.g. Reviewed capability risk with the compliance team"
                />
              </FieldGroup>
              <div className="flex items-center gap-2">
                <Button variant="primary" size="sm" loading={savingAutonomy} onClick={handleSetAutonomy}>
                  Set autonomy ceiling
                </Button>
                <Button variant="ghost" size="sm" onClick={() => { setPendingLevel(null); setAutonomyReason(''); }}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
          {profileLoading && <p className="text-[11.5px] text-v2-ink-muted m-0">Loading current autonomy…</p>}
        </div>
      </Card>

      {/* 2 · Trust baseline (conformance) */}
      <Card padding="none">
        <div className="flex items-center gap-3 px-4 pt-4 pb-3 border-b border-v2-border-subtle">
          <Target size={16} className="text-v2-cyan" />
          <h3 className="text-[14px] font-bold tracking-tight m-0">Trust baseline</h3>
          <Pill tone={baselineSet ? 'success' : 'muted'} size="sm">
            {baselineSet ? 'Registered' : 'Not registered'}
          </Pill>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-[12.5px] text-v2-ink-muted m-0">
            The conformance baseline (The Crossing) is the reference the agent's live behavior is
            scored against. Conformance is blended into the unified Trust Score during Operate.
          </p>

          {/* An ExteLet executes off-platform with no deterministic source workflow,
              so its oracle is a GOLDEN baseline: representative input → expected
              output, registered below. On a matching governed call the agent's
              output is scored against it. The settings here tune HOW conformance
              is compared + weighted; the golden baseline is WHAT it's scored
              against. Advisory — never blocks Execution-Ready. */}
          <div className="rounded-v2-md px-3 py-2 text-[12px] flex items-start gap-2"
            style={{
              background: goldenList.length > 0 ? 'var(--v2-success-soft)' : 'var(--v2-warning-soft)',
              color: goldenList.length > 0 ? 'var(--v2-success-strong)' : 'var(--v2-warning)',
            }}>
            <ShieldAlert size={14} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>
              {goldenList.length > 0 ? (
                <>
                  <strong>{goldenList.length} golden baseline{goldenList.length === 1 ? '' : 's'} registered.</strong>{' '}
                  Governed calls whose input matches are conformance-scored against the expected output.
                  Conformance still only affects the Trust Score once <strong>Conformance weight &gt; 0</strong>.
                </>
              ) : (
                <>
                  <strong>No golden baseline yet — conformance is inert.</strong> Register one below
                  (representative input → expected output). Until then the settings save but there is
                  nothing to score against. Only calls whose input matches a registered baseline are scored.
                </>
              )}
            </span>
          </div>
          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
            <FieldGroup>
              <Label htmlFor="cf-mode">Comparator</Label>
              <Select id="cf-mode" value={baselineForm.conformanceComparatorMode}
                onChange={(e) => setBaselineForm((p) => ({ ...p, conformanceComparatorMode: e.target.value }))}>
                <option value="exact_match">Exact match</option>
                <option value="equivalence">Equivalence</option>
                <option value="tolerance_banded">Tolerance banded</option>
                <option value="custom">Custom</option>
              </Select>
            </FieldGroup>
            <FieldGroup>
              <Label htmlFor="cf-required">Required conformance (%)</Label>
              <Input id="cf-required" type="number" min="0" max="100" value={baselineForm.requiredConformance}
                onChange={(e) => setBaselineForm((p) => ({ ...p, requiredConformance: e.target.value }))} />
            </FieldGroup>
            <FieldGroup>
              <Label htmlFor="cf-weight">Conformance weight (0–1)</Label>
              <Input id="cf-weight" type="number" min="0" max="1" step="0.05" value={baselineForm.conformanceWeight}
                onChange={(e) => setBaselineForm((p) => ({ ...p, conformanceWeight: e.target.value }))} />
            </FieldGroup>
            <FieldGroup>
              <Label htmlFor="cf-sample">Sample size</Label>
              <Input id="cf-sample" type="number" min="1" value={baselineForm.conformanceSampleSize}
                onChange={(e) => setBaselineForm((p) => ({ ...p, conformanceSampleSize: e.target.value }))} />
            </FieldGroup>
            <FieldGroup>
              <Label htmlFor="cf-shadow">Shadow conformance</Label>
              <Select id="cf-shadow" value={baselineForm.shadowConformanceEnabled}
                onChange={(e) => setBaselineForm((p) => ({ ...p, shadowConformanceEnabled: e.target.value }))}>
                <option value="true">On</option>
                <option value="false">Off</option>
              </Select>
            </FieldGroup>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" loading={savingBaseline} onClick={handleRegisterBaseline}>
              {baselineSet ? 'Update conformance settings' : 'Save conformance settings'}
            </Button>
            {configLoading && <span className="text-[11.5px] text-v2-ink-muted">Loading…</span>}
          </div>

          {/* Golden baseline (reference oracle) — the WHAT conformance scores against. */}
          <div className="border-t border-v2-border-subtle pt-3 mt-1 space-y-3">
            <div className="flex items-center gap-2">
              <h4 className="text-[12.5px] font-bold m-0">Golden baseline (reference oracle)</h4>
              <Pill tone={goldenList.length > 0 ? 'success' : 'muted'} size="sm">
                {goldenLoading ? '…' : `${goldenList.length} registered`}
              </Pill>
            </div>
            <p className="text-[11.5px] text-v2-ink-muted m-0">
              Register a representative input and the output it should produce. On a protected call whose
              input matches, the agent's output is scored against this expected output — no re-execution.
            </p>
            <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
              <FieldGroup>
                <Label htmlFor="gb-input">Representative input (JSON)</Label>
                <Textarea id="gb-input" rows={5} placeholder={'{"recordId": 42, "action": "submit"}'}
                  value={goldenForm.input}
                  onChange={(e) => setGoldenForm((p) => ({ ...p, input: e.target.value }))} />
              </FieldGroup>
              <FieldGroup>
                <Label htmlFor="gb-output">Expected output (JSON)</Label>
                <Textarea id="gb-output" rows={5} placeholder={'{"status": "accepted"}'}
                  value={goldenForm.goldenOutput}
                  onChange={(e) => setGoldenForm((p) => ({ ...p, goldenOutput: e.target.value }))} />
              </FieldGroup>
            </div>
            <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
              <FieldGroup>
                <Label htmlFor="gb-comparator">Comparator override (optional)</Label>
                <Select id="gb-comparator" value={goldenForm.comparatorMode}
                  onChange={(e) => setGoldenForm((p) => ({ ...p, comparatorMode: e.target.value }))}>
                  <option value="">Use agent default</option>
                  <option value="exact_match">Exact match</option>
                  <option value="equivalence">Equivalence</option>
                  <option value="tolerance_banded">Tolerance banded</option>
                </Select>
              </FieldGroup>
              <FieldGroup>
                <Label htmlFor="gb-desc">Description (optional)</Label>
                <Input id="gb-desc" value={goldenForm.description}
                  onChange={(e) => setGoldenForm((p) => ({ ...p, description: e.target.value }))} />
              </FieldGroup>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="primary" size="sm" loading={savingGolden}
                disabled={!goldenForm.input.trim() || !goldenForm.goldenOutput.trim()}
                onClick={handleRegisterGolden}>
                Register golden baseline
              </Button>
            </div>

            {goldenList.length > 0 && (
              <div className="space-y-1">
                {goldenList.map((b) => (
                  <div key={b.id} className="flex items-center gap-2 text-[11.5px] py-1 border-b border-v2-border-subtle/50">
                    <span className="font-mono-v2 text-v2-ink-muted">{String(b.inputHash || '').slice(0, 10)}…</span>
                    <span className="text-v2-ink-secondary truncate">{b.description || '(no description)'}</span>
                    <span className="ml-auto text-v2-ink-muted">{b.comparatorMode || 'default'}</span>
                    <button className="v2-focus-ring text-[11.5px] font-semibold text-v2-danger hover:text-v2-danger-strong"
                      onClick={() => handleDeleteGolden(b.id)} title="Delete this golden baseline">Remove</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* 3 · Scoring config (read-only; full editing on the scoring surface) */}
      <Card padding="none">
        <div className="flex items-center gap-3 px-4 pt-4 pb-3 border-b border-v2-border-subtle">
          <SlidersHorizontal size={16} className="text-v2-cyan" />
          <h3 className="text-[14px] font-bold tracking-tight m-0">Scoring config</h3>
          <span className="text-[11px] text-v2-ink-muted">Org → Tenant → Agent inheritance</span>
          <a href="/native-trust?tab=scoring"
            className="ml-auto v2-focus-ring text-[11.5px] font-semibold text-v2-action hover:text-v2-action-strong">
            Edit full config &rarr;
          </a>
        </div>
        <div className="p-4">
          {configLoading ? (
            <p className="text-[12px] text-v2-ink-muted m-0">Resolving effective scoring config…</p>
          ) : config ? (
            <div className="grid gap-3 text-[12.5px]" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
              <Stat label="Scope" value={config.configScope || '—'} />
              <Stat label="Window" value={`${config.windowDays ?? 90} days`} />
              <Stat label="Self-autonomous ≥" value={`${config.selfAutonomousThreshold ?? 98}%`} />
              <Stat label="Supervised ≥" value={`${config.supervisedThreshold ?? 90}%`} />
              <Stat label="Restricted ≥" value={`${config.restrictedThreshold ?? 75}%`} />
              <Stat label="Retention" value={`${config.trustHistoryRetentionDays ?? 1095} days`} />
            </div>
          ) : (
            <p className="text-[12px] text-v2-ink-muted m-0">
              No scoring config resolved — system defaults apply (90-day window, 3-year retention).
            </p>
          )}
        </div>
      </Card>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-v2-sm bg-v2-muted px-3 py-2">
      <div className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted">{label}</div>
      <div className="text-[13px] font-semibold text-v2-ink mt-0.5 tabular-nums">{value}</div>
    </div>
  );
}
